import React, { useMemo } from "react";
import { format, parseISO, isValid } from "date-fns";
import GlobalDataTable, { TableRow, TableCell } from "../../../../global/GlobalDataTable";
import GlobalDirectoryPaginationSection from "../../../../global/directory/GlobalDirectoryPaginationSection";
import { getScheduleColumnDefs } from "../../utils/scheduleColumnDefs";
import { scheduleIdText } from "../../utils/scheduleHelpers";
import ScheduleStatusBadge from "./ScheduleStatusBadge";
import CreatedByCell from "./CreatedByCell";
import ScheduleActions from "./ScheduleActions";
import "../../styles/scheduleTable.css";
import "../../../user-builder/styles/userBuilderTable.css";

function formatDateCell(value) {
  if (!value) return "—";
  try {
    const d = typeof value === "string" && value.includes("T")
      ? parseISO(value)
      : parseISO(String(value).slice(0, 10));
    return isValid(d) ? format(d, "dd MMM yyyy") : String(value).slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
}

function formatTimeCell(value) {
  if (!value) return "—";
  const s = String(value);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function renderCell(columnId, row) {
  switch (columnId) {
    case "schedule_id":
      return scheduleIdText(row) || "—";
    case "schedule_date":
      return formatDateCell(row.schedule_date);
    case "schedule_time":
      return formatTimeCell(row.schedule_time);
    case "schedule_title":
      return <span style={{ fontWeight: 500 }}>{row.schedule_title || "—"}</span>;
    case "schedule_type_name":
      return row.schedule_type_name || "—";
    case "assigned_to_name":
      return row.assigned_to_name || "—";
    case "priority_name":
      return row.priority_name || "—";
    case "status":
      return <ScheduleStatusBadge schedule={row} />;
    case "created_by_name":
      return <CreatedByCell row={row} />;
    case "actions":
      return null;
    default:
      return row[columnId] ?? "—";
  }
}

export default function ScheduleTable({
  rows = [],
  loading = false,
  isAdmin = false,
  permissions,
  page = 1,
  pageSize = 15,
  total = 0,
  totalPages = 1,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onComplete,
  onCancel,
  emptyMessage = "No schedules match your search.",
  columnVisibilityOverride = null,
  showPaginationFooter = false,
}) {
  const allColumns = getScheduleColumnDefs(isAdmin);

  const columns = useMemo(() => {
    if (!columnVisibilityOverride) return allColumns;
    return allColumns.filter((col) => {
      if (col.id === "actions") return true;
      return columnVisibilityOverride[col.id] !== false;
    });
  }, [allColumns, columnVisibilityOverride]);

  const headers = useMemo(
    () =>
      columns.map((c) => ({
        key: c.id,
        label: c.label,
        className: c.id === "actions" ? "ub-table__col-actions" : "",
      })),
    [columns],
  );

  if (loading) {
    return <p className="schedule-table-loading">Loading schedules…</p>;
  }

  return (
    <>
      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Schedules"
        headers={headers}
      >
        {rows.map((row) => (
          <TableRow key={row.id}>
            {columns.map((col) => (
              <TableCell key={col.id} className={col.id === "actions" ? "ub-table__col-actions" : ""}>
                {col.id === "actions" ? (
                  <ScheduleActions
                    schedule={row}
                    permissions={permissions}
                    isAdmin={isAdmin}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onComplete={onComplete}
                    onCancel={onCancel}
                  />
                ) : (
                  renderCell(col.id, row)
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              {emptyMessage}
            </td>
          </tr>
        ) : null}
      </GlobalDataTable>
      {showPaginationFooter && total > 0 ? (
        <GlobalDirectoryPaginationSection
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      ) : null}
    </>
  );
}
